import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, Wallet, ArrowDownCircle, ArrowUpCircle,
  BarChart2, Users, RefreshCw, Search, FolderOpen, Activity,
} from 'lucide-react';
import { fetchFinancialAdminStats, fetchAdminProjects } from './hooks/hooks';

const NAV  = '#001f3f';
const TEAL = '#0d9488';
const fmt  = (n) => `TZS ${Number(n ?? 0).toLocaleString()}`;
const fmtN = (n) => Number(n ?? 0).toLocaleString();
const timeAgo = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Atoms ────────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
         style={{ backgroundColor: accent + '18' }}>
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-lg font-bold text-slate-800 leading-tight">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const ProfitBadge = ({ balance }) => {
  const isProfit = balance >= 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border
      ${isProfit ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
      {isProfit ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {isProfit ? 'PROFIT' : 'LOSS'}
    </span>
  );
};

// ─── Mini bar chart ───────────────────────────────────────────────────────────
const MiniBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-100 rounded-full h-2">
        <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-slate-500 w-20 text-right">{fmt(value)}</span>
    </div>
  );
};

// ─── Monthly trend table ──────────────────────────────────────────────────────
const TrendTable = ({ trend }) => {
  if (!trend?.length) return null;
  const maxDeposit = Math.max(...trend.map(t => t.deposits), 1);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <BarChart2 size={16} style={{ color: NAV }} />
        <h3 className="text-sm font-bold text-slate-800">6-Month Platform Trend</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              {['Month', 'Deposits', 'Withdrawals', 'Investments', 'Earnings'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {trend.map((row, i) => (
              <tr key={i} className={`hover:bg-slate-50 ${i === trend.length - 1 ? 'bg-teal-50/40' : ''}`}>
                <td className="px-4 py-3 font-semibold text-slate-700 text-xs">{row.month}</td>
                <td className="px-4 py-3"><MiniBar value={row.deposits}    max={maxDeposit} color="#10b981" /></td>
                <td className="px-4 py-3"><MiniBar value={row.withdrawals} max={maxDeposit} color="#ef4444" /></td>
                <td className="px-4 py-3"><MiniBar value={row.investments} max={maxDeposit} color="#6366f1" /></td>
                <td className="px-4 py-3"><MiniBar value={row.earnings}    max={maxDeposit} color="#f59e0b" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Category breakdown ───────────────────────────────────────────────────────
const CatBreakdown = ({ title, cats, color }) => {
  if (!cats?.length) return null;
  const max = cats[0]?.total || 1;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h4 className="text-sm font-bold text-slate-700 mb-4">{title}</h4>
      <div className="space-y-3">
        {cats.map((c, i) => (
          <div key={i}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600 capitalize">{c.category?.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-slate-700">{fmt(c.total)}</span>
            </div>
            <MiniBar value={c.total} max={max} color={color} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Platform overview tab ────────────────────────────────────────────────────
const PlatformTab = ({ stats }) => {
  const p = stats?.platform || {};
  const pers = stats?.personal || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={ArrowDownCircle} label="Total Deposits"    value={fmt(p.total_deposits)}    accent="#10b981" sub={`${fmtN(p.tx_this_month)} tx this month`} />
        <StatCard icon={ArrowUpCircle}   label="Total Withdrawals" value={fmt(p.total_withdrawals)} accent="#ef4444" />
        <StatCard icon={TrendingUp}      label="Total Investments"  value={fmt(p.total_investments)} accent="#6366f1" sub={`${fmtN(p.tx_this_week)} tx this week`} />
        <StatCard icon={Wallet}          label="Total Earnings"     value={fmt(p.total_earnings)}    accent="#f59e0b" />
        <StatCard icon={Activity}        label="Total Transactions" value={fmtN(p.tx_total)}         accent={NAV} />
        <StatCard icon={Users}           label="Active Projects"    value={fmtN(pers.active_projects)} accent={TEAL} sub={`${fmtN(pers.total_projects)} total`} />
      </div>

      <TrendTable trend={stats?.monthly_trend} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CatBreakdown title="Top Expense Categories" cats={pers.top_expense_cats} color="#ef4444" />
        <CatBreakdown title="Top Income Categories"  cats={pers.top_income_cats}  color="#10b981" />
      </div>
    </div>
  );
};

// ─── Personal projects tab ────────────────────────────────────────────────────
const ProjectsTab = () => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-projects'],
    queryFn:  fetchAdminProjects,
  });

  const projects = (data?.data || []).filter(p => {
    const matchSearch = !search ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.user_email?.toLowerCase().includes(search.toLowerCase());
    if (filter === 'profit') return matchSearch && p.balance >= 0;
    if (filter === 'loss')   return matchSearch && p.balance < 0;
    if (filter === 'active') return matchSearch && p.is_active;
    return matchSearch;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex gap-1">
          {[['all','All'],['active','Active'],['profit','Profit'],['loss','Loss']].map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${filter === v ? 'bg-teal-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={refetch} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
          <RefreshCw size={15} className="text-slate-500" />
        </button>
        <span className="text-xs text-slate-500">{fmtN(data?.total)} total</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Project', 'Owner', 'Budget', 'Mapato', 'Matumizi', 'Balance', 'Entries', 'Status', 'Created'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {projects.slice(0, 100).map(p => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate">{p.name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{p.user_email}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{fmt(p.budget)}</td>
                  <td className="px-4 py-3 text-emerald-600 font-semibold text-xs whitespace-nowrap">{fmt(p.total_income)}</td>
                  <td className="px-4 py-3 text-red-600 font-semibold text-xs whitespace-nowrap">{fmt(p.total_expense)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${p.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmt(Math.abs(p.balance))}
                      </span>
                      <ProfitBadge balance={p.balance} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600">{p.entry_count}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.is_active ? 'Active' : 'Closed'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{timeAgo(p.created_at)}</td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No projects found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'platform', label: 'Platform Overview' },
  { key: 'projects', label: 'Personal Projects' },
];

export default function FinancialTrackerAdmin() {
  const [tab, setTab] = useState('platform');
  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['financial-admin-stats'],
    queryFn:  fetchFinancialAdminStats,
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Financial Tracker</h2>
          <p className="text-xs text-slate-400 mt-0.5">Platform transactions, trends & personal finance overview</p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors
              ${tab === t.key
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && tab === 'platform' ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <>
          {tab === 'platform' && <PlatformTab stats={stats} />}
          {tab === 'projects' && <ProjectsTab />}
        </>
      )}
    </div>
  );
}
